import clsx from "clsx"

import { faIcon } from "@utils/faIcon"

import { ExtraTouchArea, Skeleton } from "@particles/index"

import { Icon, IconButton, Tooltip } from "@atoms"
import { IconSize } from "@atoms/Icon/Icon.types"

import styles from "./Badge.module.scss"
import { AvatarBadgeProps, BadgeProps, IconBadgeProps } from "./Badge.types"

const iconSizesBySize: Record<BadgeProps["size"], IconSize> = {
    sm: 12,
    md: 14,
    lg: 16,
}

const skeletonHeightsBySize: Record<BadgeProps["size"], number> = {
    sm: 9,
    md: 10,
    lg: 10,
}

const extraTouchAreasBySize: Record<BadgeProps["size"], number> = {
    sm: 4,
    md: 8,
    lg: 8,
}

export default function Badge(props: BadgeProps) {
    const {
        variant = "default",
        size,

        isDismissable = false,
        onDismiss,

        tooltip,

        className,
        buttonClassName,
        contentClassName,

        isLoading = false,
        loadingWidth,

        children,
    } = props

    const icon: IconBadgeProps["icon"] = (props as IconBadgeProps)?.icon
    const iconClassName: IconBadgeProps["iconClassName"] = (props as IconBadgeProps)?.iconClassName
    const iconRotation: IconBadgeProps["iconRotation"] = (props as IconBadgeProps)?.iconRotation
    const iconContainer: IconBadgeProps["iconContainer"] = (props as IconBadgeProps)?.iconContainer ?? "fixedHeight"
    const avatar: AvatarBadgeProps["avatar"] = (props as AvatarBadgeProps)?.avatar

    return (
        <Tooltip body={tooltip} isDisabled={tooltip == null} defaultPlacement="bottom">
            <div
                className={clsx(
                    styles.base,
                    styles[`${size}Size`],
                    { [styles.isDismissable]: isDismissable && !isLoading },
                    className,
                )}
                style={isLoading && loadingWidth ? { width: loadingWidth } : undefined}
            >
                <div className={styles.inner}>
                    {variant === "avatar" && (
                        <>
                            {isLoading ? (
                                <Skeleton circle={true} height={16} width={16} enableAnimation={true} />
                            ) : (
                                avatar
                            )}
                        </>
                    )}
                    {variant === "icon" && (
                        <>
                            {isLoading ? (
                                <Skeleton
                                    circle={true}
                                    height={iconSizesBySize[size]}
                                    width={iconSizesBySize[size]}
                                    enableAnimation={true}
                                />
                            ) : (
                                <Icon
                                    icon={icon}
                                    size={iconSizesBySize[size]}
                                    container={iconContainer}
                                    className={iconClassName}
                                    rotation={iconRotation}
                                />
                            )}
                        </>
                    )}
                    {(isLoading || children != null) && (
                        <div className={clsx(styles.content, contentClassName)}>
                            {isLoading ? (
                                <Skeleton
                                    height={skeletonHeightsBySize[size]}
                                    width={loadingWidth ? "100%" : 40}
                                    borderRadius={0}
                                    enableAnimation={true}
                                />
                            ) : (
                                children
                            )}
                        </div>
                    )}
                </div>
                {isDismissable && !isLoading && (
                    <ExtraTouchArea extraTouchArea={extraTouchAreasBySize[size]}>
                        <IconButton
                            size={size === "lg" ? "xs" : "xxs"}
                            variant="muted"
                            icon={faIcon({ name: "xmark", style: "regular" })}
                            onClick={onDismiss}
                            className={buttonClassName}
                        />
                    </ExtraTouchArea>
                )}
            </div>
        </Tooltip>
    )
}
